import React from 'react';

/**
 * NeuroGen Logo Component
 * 
 * Usage:
 *   <NeuroGenLogo />                          -- icon + styled text (default)
 *   <NeuroGenLogo size={48} />                -- custom size
 *   <NeuroGenLogo iconOnly />                 -- icon only, no text
 *   <NeuroGenLogo variant="light" />          -- for dark backgrounds (white text)
 *   <NeuroGenLogo variant="dark" />           -- for light backgrounds (dark text)
 *   <NeuroGenLogo subtitle="IOM QA PORTAL" /> -- custom subtitle
 *   <NeuroGenLogo centered />                 -- stacks icon above text, centered
 */

const NetworkIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="64" height="64" rx="14" fill="#1B2A4A" />
    {/* Connections */}
    <line x1="14" y1="18" x2="28" y2="26" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="14" y1="18" x2="36" y2="14" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="36" y1="14" x2="50" y2="20" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="36" y1="14" x2="28" y2="26" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="50" y1="20" x2="44" y2="34" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="28" y1="26" x2="44" y2="34" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="28" y1="26" x2="18" y2="40" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="28" y1="26" x2="36" y2="44" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="44" y1="34" x2="36" y2="44" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="18" y1="40" x2="36" y2="44" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="18" y1="40" x2="28" y2="52" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="36" y1="44" x2="28" y2="52" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="36" y1="44" x2="50" y2="48" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    <line x1="44" y1="34" x2="50" y2="48" stroke="#6CB4EE" strokeWidth="1" opacity="0.35" />
    {/* Nodes */}
    <circle cx="14" cy="18" r="3" fill="#4A90D9" opacity="0.7" />
    <circle cx="36" cy="14" r="3.5" fill="#4A90D9" />
    <circle cx="50" cy="20" r="2.5" fill="#4A90D9" opacity="0.65" />
    <circle cx="28" cy="26" r="5" fill="#5BA3E6" />
    <circle cx="44" cy="34" r="4" fill="#4A90D9" />
    <circle cx="18" cy="40" r="3" fill="#4A90D9" opacity="0.75" />
    <circle cx="36" cy="44" r="4.5" fill="#5BA3E6" opacity="0.9" />
    <circle cx="28" cy="52" r="2.5" fill="#4A90D9" opacity="0.6" />
    <circle cx="50" cy="48" r="3" fill="#4A90D9" opacity="0.7" />
    {/* Highlights */}
    <circle cx="27" cy="25" r="1.8" fill="white" opacity="0.18" />
    <circle cx="35" cy="43" r="1.5" fill="white" opacity="0.15" />
  </svg>
);

const NeuroGenLogo = ({ 
  size = 48, 
  iconOnly = false, 
  variant = 'light',
  subtitle = 'QA PORTAL',
  centered = false,
  style = {}
}) => {
  const textColor = variant === 'light' ? '#FFFFFF' : '#1B2A4A';
  const accentColor = variant === 'light' ? '#60A5FA' : '#2563EB';
  const subtitleColor = variant === 'light' ? '#6B84A8' : '#8896AB';

  const titleSize = Math.round(size * 0.45);
  const subtitleSize = Math.round(size * 0.2);

  if (centered) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: size * 0.2, ...style }}>
        <NetworkIcon size={size} />
        {!iconOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{
              fontSize: titleSize,
              fontWeight: 700,
              color: textColor,
              letterSpacing: '-0.5px',
              fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
              lineHeight: 1.1,
            }}>
              Neuro<span style={{ color: accentColor }}>Gen</span>
            </span>
            {subtitle && (
              <span style={{
                fontSize: subtitleSize,
                color: subtitleColor,
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
                lineHeight: 1.4,
                marginTop: 2,
              }}>
                {subtitle}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.25, ...style }}>
      <NetworkIcon size={size} />
      {!iconOnly && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{
            fontSize: titleSize,
            fontWeight: 700,
            color: textColor,
            letterSpacing: '-0.5px',
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            lineHeight: 1.1,
          }}>
            Neuro<span style={{ color: accentColor }}>Gen</span>
          </span>
          {subtitle && (
            <span style={{
              fontSize: subtitleSize,
              color: subtitleColor,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
              lineHeight: 1.2,
            }}>
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export { NetworkIcon };
export default NeuroGenLogo;
